import {
  MutationUpdaterFunction,
  DefaultContext,
  ApolloCache,
  ApolloClient,
} from '@apollo/client'
import cloneDeep from 'lodash/cloneDeep'

import {
  GetBookingAdventuresDocument,
  GetBookingAdventuresIndexDocument,
} from '../../../../hooks/useQueries'

type MutationArgs = Exact<{
  bookingId: string
  adventureId: string
  source?: string
}>

type BuildMutationOptionsArgs = {
  itemId: string
  bookingId: string
  apolloClient: ApolloClient<object>
}

const buildUpdateCacheOnAddItemToBooking =
  ({
    itemId,
    bookingId,
  }: BuildMutationOptionsArgs): MutationUpdaterFunction<
    GQL_AddAdventureToBookingMutation,
    MutationArgs,
    DefaultContext,
    ApolloCache<any>
  > =>
  (cache) => {
    const prevData = cache.readQuery<GQL_GetBookingAdventuresIndexQuery>({
      query: GetBookingAdventuresIndexDocument,
    })

    const nextData = cloneDeep(prevData)

    cache.writeQuery({
      query: GetBookingAdventuresIndexDocument,
      data: {
        ...nextData,
        currentUser: {
          ...(nextData?.currentUser || {}),
          bookingAdventuresIndex: {
            ...(nextData?.currentUser?.bookingAdventuresIndex || {}),
            [itemId]: [
              ...(nextData?.currentUser?.bookingAdventuresIndex?.[itemId] ||
                []),
              bookingId,
            ],
          },
        },
      },
    })
  }

const buildUpdateCacheOnRemoveItemFromBooking =
  ({
    itemId,
    bookingId,
  }: BuildMutationOptionsArgs): MutationUpdaterFunction<
    GQL_RemoveAdventureFromBookingMutation,
    MutationArgs,
    DefaultContext,
    ApolloCache<any>
  > =>
  (cache) => {
    const prevData = cache.readQuery<GQL_GetBookingAdventuresIndexQuery>({
      query: GetBookingAdventuresIndexDocument,
    })

    const nextData = cloneDeep(prevData)

    cache.writeQuery({
      query: GetBookingAdventuresIndexDocument,
      data: {
        ...nextData,
        currentUser: {
          ...(nextData?.currentUser || {}),
          bookingAdventuresIndex: {
            ...(nextData?.currentUser?.bookingAdventuresIndex || {}),
            [itemId]: [
              ...(nextData?.currentUser?.bookingAdventuresIndex?.[
                itemId
              ]?.filter((id: string) => id !== bookingId) || []),
            ],
          },
        },
      },
    })
  }

const buildOptimisticResponseOnAddItemToBooking =
  ({ itemId }: BuildMutationOptionsArgs) =>
  () => {
    return {
      __typename: 'Mutation',
      createBookingAdventure: {
        __typename: 'CreateBookingAdventurePayload',
        adventure: {
          __typename: 'StoryAdventure',
          id: itemId,
        },
      },
    } as const
  }

const buildOptimisticResponseOnRemoveItemFromBooking =
  ({ bookingId, apolloClient }: BuildMutationOptionsArgs) =>
  () => {
    const bookingAdventures =
      apolloClient.cache.readQuery<GQL_GetBookingAdventuresQuery>({
        query: GetBookingAdventuresDocument,
      })

    const adventures =
      bookingAdventures?.booking?.adventures?.filter(
        ({ id }) => id !== bookingId,
      ) || []

    return {
      __typename: 'Mutation',
      destroyBookingAdventure: {
        __typename: 'DestroyBookingAdventurePayload',
        adventures,
      },
    } as const
  }

export const buildMutationOptionsAddItemToBooking = ({
  itemId,
  bookingId,
  apolloClient,
}: BuildMutationOptionsArgs) => ({
  update: buildUpdateCacheOnAddItemToBooking({
    itemId,
    bookingId,
    apolloClient,
  }),
  optimisticResponse: buildOptimisticResponseOnAddItemToBooking({
    itemId,
    bookingId,
    apolloClient,
  }),
})

export const buildMutationOptionsRemoveItemFromBooking = ({
  itemId,
  bookingId,
  apolloClient,
}: BuildMutationOptionsArgs) => ({
  update: buildUpdateCacheOnRemoveItemFromBooking({
    itemId,
    bookingId,
    apolloClient,
  }),
  optimisticResponse: buildOptimisticResponseOnRemoveItemFromBooking({
    itemId,
    bookingId,
    apolloClient,
  }),
})
